import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Icon from "@mdi/react";
import { debounce } from "lodash";
import {
  mdiArrowTopRight,
  mdiClose,
  mdiMenuDown,
  mdiMenuUp,
  mdiRefresh,
} from "@mdi/js";
import moment from "moment";
import { fetchGet, toQueryString } from "../../../../service/api_client";
import {
  useToast,
  ToastType,
} from "../../../../context_providers/toast/toast_ctx";
import { FieldType } from "../../../../procurement/components/create_view/form_field";
import LoadingWidget from "../../../../context_providers/modal/loader";
import SearchFieldWidget from "../../../../ui/new_components/search_field";
import ModalDialog from "../../../../ui/new_components/common/modal";
import DrawerModal, {
  DrawerModalLoaction,
} from "../../../../ui/new_components/common/drawer_modal";
import {
  IntransitStockJournalModel,
  UniqueProductModel,
} from "../../models/stock_transfer_model";
import TableView from "../../../../procurement/components/table_comp";
import { formatAmountWithCommas } from "../../../../ui/pages/content_panel/utils/helpers";

interface StockTransferJournalListScreenProps {
  records: IntransitStockJournalModel[];
  onClick: (id: string) => void;
  sort_by: string;
  sort_order: number;
  pages: number;
  options: []; // Replace with actual type
  handleSort: (data: { sort: string; sort_order: number }) => void;
  handlePageChange: (page: number) => void;
  // handleStatusFilterChange: (selectedOptions: string[]) => void;
}

interface StockListFilter {
  stock_transfer_id?: string;
  from_warehouse?: string;
  to_warehouse?: string;
  sender_name?: string;
  sender_department?: string;
  receiver_department?: string;
  receiver_name?: string;
  search?: string;
  page: number;
  count: number;
  all?: any;
  asOfDate?: string;
}

const defaultFilter = (): StockListFilter => {
  return {
    page: 1,
    count: 100,
  };
};

const IntransitStockTransferListScreen: FunctionComponent<
  StockTransferJournalListScreenProps
> = () => {
  const navigation = useHistory();
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/journal/sender_transfer/in-transit`;
  const { showToast } = useToast();
  const [page, setPage] = useState(1);
  const [count] = useState(50);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState<IntransitStockJournalModel[]>([]);
  const [filter, setFilter] = useState<StockListFilter>(defaultFilter());
  const [showUniqueIds, setShowUniqueIds] = useState(false);
  const [selectedStockJoural, setSelectedStockJournal] =
    useState<IntransitStockJournalModel | null>(null);
  const [ShowHistory, setShowHistory] = useState<any>();
  const [filterCollaped, setFilterCollapsed] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandler = useCallback(
    debounce((prop: StockListFilter) => {
      if (prop) fetchData(prop);
    }, 300),
    [],
  );
  useEffect(() => {
    debouncedHandler(filter);
  }, [filter, debouncedHandler]);

  const [selectedRows, setSelectedRows] = useState<UniqueProductModel[]>([]);
  const toggleRowSelection = (row: UniqueProductModel) => {
    console.log("Function Called");
    const exists = selectedRows.find((r) => r.unique_id === row.unique_id);
    if (exists) {
      setSelectedRows(
        selectedRows.filter((r) => r.unique_id !== row.unique_id),
      );
    } else {
      setSelectedRows([...selectedRows, row]);
    }
  };

  useEffect(() => {
    console.log("Selected Rows", selectedRows);
  }, [selectedRows]);

  useEffect(() => {
    if (!data.length) return;

    const updatedRows = data.map((entry) =>
      Array.from({ length: entry?.unit_no || 0 }, (_, index) => ({
        unique_id: entry?.uniqueIds?.[index]?.unique_id || "", // Get existing unique_id if present
        item_name: entry?.item?.name || "",
        status: entry?.uniqueIds?.[index]?.status || "",
      })),
    );

    // Flatten the array if needed
    // const flattenedRows = updatedRows.flat();

    // setInitialRows(flattenedRows);
    // setSelectedRows(flattenedRows.map((item) => item.status === "Confirm"));

    setData((prevData) => {
      if (!prevData.length) return prevData;

      return prevData.map((entry, index) => ({
        ...entry,
        uniqueIds: updatedRows[index],
      }));
    });
  }, []);
  const fetchData = async (prop: StockListFilter) => {
    setLoading(true);
    try {
      const res = await fetchGet(url + toQueryString(prop));
      if (res.success) {
        setData(res.data.data);
        if (res.data.metadata) {
          setTotal(res.data.metadata.total);
        }
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const getIntransitExport = async () => {
    try {
      setLoading(true);
      const searchProp = { ...filter };
      searchProp.all = true;
      const response = await ExportInTransit(searchProp);

      if (response.success) {
        showToast({
          type: ToastType.success,
          text: response.message,
        });
      } else {
        showToast({
          type: ToastType.error,
          text: response.message,
        });
      }
    } catch (error: any) {
      console.error("Export initiation error:", error);

      showToast({ type: ToastType.error, text: error.error });
    } finally {
      setLoading(false);
    }
  };

  const ExportInTransit = async (props: StockListFilter) => {
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/journal/sender_transfer/in-transit/export`;

    const res = await fetchGet(url + toQueryString(props));
    return res;
  };

  const exportOnlyUniqueIDsAsCSV = (): void => {
    // If no rows selected, return early
    if (selectedRows.length === 0) return;
    const headers = ["unique_id", "item_name"];
    let csvContent = headers.join(",") + "\n";

    // Add each unique ID as a row
    selectedRows.forEach((item) => {
      const row = [
        // Wrap values in quotes to handle commas within fields
        `"${item.unique_id}"`,
        `"${item.item_name}"`,
      ];
      csvContent += row.join(",") + "\n";
    });

    // Create blob with CSV content
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    // Create download link and trigger download
    const link = document.createElement("a");
    link.href = url;
    link.download = "stock_journal_export.csv";
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {showUniqueIds && (
        <ModalDialog
          show={showUniqueIds}
          title={"Product Details"}
          onClose={() => {
            setShowUniqueIds(false);
            setSelectedRows([]);
          }}
        >
          <div className="bg-white p-6 w-96">
            <div className="flex justify-between">
              <h2 className="text-md font-semibold mb-4">Product List</h2>
              <div className="">
                <button
                  className="bg-green-600 text-white p-1 rounded-md"
                  disabled={selectedRows.length === 0 ? true : false}
                  onClick={exportOnlyUniqueIDsAsCSV}
                >
                  Export
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <div className="max-h-60 overflow-y-auto">
                {" "}
                {/* Adjust height as needed */}
                <table className="w-full border border-gray-300">
                  {/* Table Head */}
                  <thead className="bg-gray-200 sticky top-0 z-10">
                    <tr>
                      {/* <th className="p-2 border bg-gray-200">
                        <input
                          type="checkbox"
                          checked={
                            (selectedRows ?? []).length ===
                              (selectedStockJoural?.uniqueIds ?? []).length &&
                            (selectedStockJoural?.uniqueIds ?? []).length > 0
                          }
                          onClick={() => {
                            if (
                              (selectedRows ?? []).length ===
                              (selectedStockJoural?.uniqueIds ?? []).length
                            ) {
                              setSelectedRows([]);
                            } else {
                              setSelectedRows(
                                selectedStockJoural?.uniqueIds ?? []
                              );
                            }
                          }}
                        />
                      </th> */}
                      <th className="p-2 border text-left bg-gray-200">
                        Unique Id
                      </th>
                      <th className="p-2 border text-left bg-gray-200">
                        Item Name
                      </th>
                    </tr>
                  </thead>

                  {/* Table Body */}
                  <tbody>
                    {(
                      selectedStockJoural?.uniqueIds?.filter(
                        (uniqueId: UniqueProductModel) =>
                          uniqueId.status !== "Confirm",
                      ) ?? []
                    ).map((row: UniqueProductModel, index: number) => (
                      <tr key={row.id} className="text-center">
                        {/* <td className="p-2 border">
                            <input
                              type="checkbox"
                              checked={
                                !!selectedRows.find(
                                  (r) => r.unique_id === row.unique_id
                                )
                              }
                              onClick={() => toggleRowSelection(row)}
                            />
                            <span className="ml-2">Confirm</span>
                          </td> */}
                        <td className="p-2 border">
                          <input
                            type="text"
                            value={row.unique_id || ""}
                            className="p-1 border rounded w-full focus:outline-none"
                            placeholder="Enter Unique Id"
                          />
                        </td>
                        <td className="p-2 border">{row.item_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </ModalDialog>
      )}
      {ShowHistory && (
        <DrawerModal
          location={DrawerModalLoaction.right}
          show={ShowHistory !== undefined}
          onClose={() => setShowHistory(undefined)}
          title="History"
        >
          {/* {console.log("id of order", ShowHistory)} */}
          {/* <ActionHistory
              voucher_id={ShowHistory}
              // show={ShowHistory !== undefined}
              // onClose={(v) => setShowHistory(undefined)}
            /> */}
        </DrawerModal>
      )}

      <div
        id="page_container"
        className="flex flex-col justify-between h-full p-1"
      >
        <section
          id="top_section"
          className="flex justify-between items-end my-1"
        >
          <h1 className="font-bold text-2xl text-gray-500 flex items-center gap-3">
            In-Transit Page
            <button
              title="Refersh"
              onClick={() => debouncedHandler({ ...filter })}
            >
              <Icon path={mdiRefresh} className="h-6 w-6"></Icon>
            </button>
          </h1>

          <div className="text-sm flex flex-wrap items-center gap-2 m-3">
            <div className="flex items-center gap-1">
              <label htmlFor="asOfDate" className="">
                As of
              </label>
              <input
                type="date"
                id="asOfDate"
                className="p-1 rounded focus:outline-none"
                value={filter.asOfDate ?? ""}
                onChange={(e) => {
                  const selectedDate = e.target.value || undefined;
                  setFilter((prevFilter) => ({
                    ...prevFilter,
                    asOfDate: selectedDate,
                    page: 1,
                  }));
                  setPage(1);
                }}
              />
              {filter.asOfDate && (
                <button
                  title="Clear date"
                  onClick={() => {
                    setFilter((prevFilter) => ({
                      ...prevFilter,
                      asOfDate: undefined,
                      page: 1,
                    }));
                    setPage(1);
                  }}
                  className="flex gap-1 items-center cursor-pointer rounded p-1 border r hover:scale-105 duration-200     transform"
                >
                  <Icon path={mdiClose} size={0.7}></Icon>
                </button>
              )}
            </div>
            <div className="flex flex-wrap text-sm items-center gap-2 ">
              <button
                onClick={() => getIntransitExport()}
                className="rounded-lg border px-2 py-1 hover:bg-gray-800 hover:text-white flex gap-2 items-center"
              >
                <Icon path={mdiArrowTopRight} size="15"></Icon> Export
              </button>
            </div>
            <div className="flex gap-2">
              <h6 className="font-normal">Filters</h6>
              <div
                onClick={() => setFilterCollapsed(!filterCollaped)}
                className="flex   items-center justify-center bg-gray-200 p-1 rounded-full hover:bg-gray-800 hover:text-white"
              >
                <Icon
                  path={filterCollaped ? mdiMenuDown : mdiMenuUp}
                  className="w-4 h-4"
                />
              </div>
            </div>
          </div>
        </section>{" "}
        {!filterCollaped && (
          <div className="grid grid-cols-2 gap-6 w-1/2 m-2">
            <div className="flex flex-col gap-1">
              <label className="text-md pt-1 font-semibold">
                Stock Transfer ID
              </label>
              <SearchFieldWidget
                defaulValue={filter?.stock_transfer_id}
                onClear={() => {
                  setFilter({
                    ...filter,
                    stock_transfer_id: undefined, // Fixed indentation
                  });
                  setPage(1);
                }}
                onSubmit={(val) => {
                  setFilter({
                    ...filter,
                    stock_transfer_id: val, // Fixed indentation
                  });
                  setPage(1);
                }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-md pt-1 font-semibold">
                From Warehouse
              </label>
              <SearchFieldWidget
                defaulValue={filter?.from_warehouse}
                onClear={() => {
                  setFilter({
                    ...filter,
                    from_warehouse: undefined, // Fixed indentation
                  });
                  setPage(1);
                }}
                onSubmit={(val) => {
                  setFilter({
                    ...filter,
                    from_warehouse: val, // Fixed indentation
                  });
                  setPage(1);
                }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-md pt-1 font-semibold">To Warehouse</label>
              <SearchFieldWidget
                defaulValue={filter?.to_warehouse}
                onClear={() => {
                  setFilter({
                    ...filter,
                    to_warehouse: undefined, // Fixed indentation
                  });
                  setPage(1);
                }}
                onSubmit={(val) => {
                  setFilter({
                    ...filter,
                    to_warehouse: val, // Fixed indentation
                  });
                  setPage(1);
                }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-md pt-1 font-semibold">Sender Name</label>
              <SearchFieldWidget
                defaulValue={filter?.sender_name}
                onClear={() => {
                  setFilter({
                    ...filter,
                    sender_name: undefined, // Fixed indentation
                  });
                  setPage(1);
                }}
                onSubmit={(val) => {
                  setFilter({
                    ...filter,
                    sender_name: val, // Fixed indentation
                  });
                  setPage(1);
                }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-md pt-1 font-semibold">
                Sender Department
              </label>
              <SearchFieldWidget
                defaulValue={filter?.sender_department}
                onClear={() => {
                  setFilter({
                    ...filter,
                    sender_department: undefined, // Fixed indentation
                  });
                  setPage(1);
                }}
                onSubmit={(val) => {
                  setFilter({
                    ...filter,
                    sender_department: val, // Fixed indentation
                  });
                  setPage(1);
                }}
              />
            </div>
            {/* <div className="flex flex-col gap-1">
              <label className="text-md pt-1 font-semibold">
                Receiver Name
              </label>
              <SearchFieldWidget
                defaulValue={filter?.receiver_name}
                onClear={() => {
                  setFilter({
                    ...filter,
                    receiver_name: undefined, // Fixed indentation
                  });
                  setPage(1);
                }}
                onSubmit={(val) => {
                  setFilter({
                    ...filter,
                    receiver_name: val, // Fixed indentation
                  });
                  setPage(1);
                }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-md pt-1 font-semibold">
                Receiver Department
              </label>
              <SearchFieldWidget
                defaulValue={filter?.receiver_department}
                onClear={() => {
                  setFilter({
                    ...filter,
                    receiver_department: undefined, // Fixed indentation
                  });
                  setPage(1);
                }}
                onSubmit={(val) => {
                  setFilter({
                    ...filter,
                    receiver_department: val, // Fixed indentation
                  });
                  setPage(1);
                }}
              />
            </div> */}
          </div>
        )}
        <TableView
          headers={[
            {
              key: "id",
              title: "ID",
              type: FieldType.string,
            },
            {
              key: "stock_transfer_id",
              title: "Stock transfer ID",
              type: FieldType.string,
            },
            {
              key: "date",
              title: "Created Date",
              type: FieldType.date,
            },
            {
              key: "sender_name",
              title: "Sender Name",
              type: FieldType.string,
            },
            {
              key: "sender_dept",
              title: "Sender Dept",
              type: FieldType.string,
            },
            {
              key: "from_warehouse",
              title: "From Warehouse",
              type: FieldType.string,
            },
            {
              key: "to_warehouse",
              title: "To Warehouse",
              type: FieldType.string,
            },
            {
              key: "item_code",
              title: "Item Code",
              type: FieldType.string,
            },
            {
              key: "item",
              title: "Item Name",
              type: FieldType.string,
            },
            {
              key: "unique_ids",
              title: "Device IDs",
              type: FieldType.string,
            },
            {
              key: "in_transit_qty",
              title: "In-Transit Qty",
              type: FieldType.number,
            },
            {
              key: "units",
              title: "Total Qty",
              type: FieldType.number,
            },
            {
              key: "received_units",
              title: "Received Qty",
              type: FieldType.number,
            },
            {
              key: "receiver_name",
              title: "Receiver Name",
              type: FieldType.string,
            },
            {
              key: "receiver_dept",
              title: "Receiver Dept",
              type: FieldType.string,
            },
            {
              key: "status",
              title: "Status",
              type: FieldType.string,
            },
          ]}
          rows={data.map((data, i) => ({
            data: data,
            values: [
              // formatISODate(data.date),
              data.id,
              data.stock_transfer_id,
              moment(data?.created_at).format("DD-MM-YYYY"),
              data.sender_name,
              data.sender_department,
              data.from_warehouse,
              data.to_warehouse,
              data.item_code,
              data.item?.name,
              // data.uniqueIds?.length + " Unique Ids",
              <span
                className="text-blue-500 cursor-pointer hover:underline"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering row click
                  if (data.uniqueIds && data.uniqueIds.length > 0) {
                    setShowUniqueIds(true);
                    setSelectedStockJournal(data);
                  }
                }}
              >
                {
                  data.uniqueIds?.filter(
                    (uniqueId) => uniqueId.status !== "Confirm",
                  ).length
                }{" "}
                Unique Ids
              </span>,
              formatAmountWithCommas(data.in_transit_units),
              formatAmountWithCommas(data?.unit_no),
              formatAmountWithCommas(data?.received_units),
              data?.receiver_name,
              data.receiver_department,
              data.status,
            ],
          }))}
          page={page}
          total={total}
          count={count}
          itemCount={data.length}
          onPageChange={setPage}
          // onClick={onSelect}
          // onShowHistory={(data) => {
          //   setShowHistory(data);
          //   // console.log(data);
          // }}
          // onDelete={onDelete}
        />
      </div>

      {loading && <LoadingWidget />}
    </>
  );
};

export default IntransitStockTransferListScreen;
